import { IconExternalLink, IconTrash } from "@tabler/icons-react";
import { Button, Form, Input, Select, Space, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { LinkType, type ResourceLink } from "@/common/model";
import {
	CustomEmpty,
	CustomIcon,
	LinkTypeBadge,
	LinkTypeLabelMap,
} from "./base";
import { toOptions } from "./base-details";
import { CardBody, CardViewer } from "./card-viewer";
import { CustomDrawer } from "./layout/workspace/custom-drawer";

const EMPTY_LINK: ResourceLink = {
	url: "",
	name: "",
	type: LinkType.GENERIC,
	description: "",
};

/** Drawer to add or edit a single resource link. Add mode = omit `onDelete`. */
export function LinkDrawer({
	open,
	initialValue,
	onSave,
	onDelete,
	onClose,
}: {
	open: boolean;
	initialValue: ResourceLink;
	onSave: (value: ResourceLink) => void;
	onDelete?: () => void;
	onClose: () => void;
}) {
	const [form] = Form.useForm<ResourceLink>();

	// Sync the form to the link being opened (or reset to empty when adding).
	useEffect(() => {
		if (open) form.setFieldsValue(initialValue);
	}, [open, initialValue, form]);

	return (
		<CustomDrawer
			title="Link"
			open={open}
			onClose={onClose}
			extra={
				<Space>
					{initialValue.url && (
						<Tooltip title="Open in a new window">
							<Button
								icon={<CustomIcon icon={IconExternalLink} />}
								type="text"
								onClick={() => window.open(initialValue.url ?? "", "_blank")}
							/>
						</Tooltip>
					)}
					{onDelete && (
						<Tooltip title="Delete link">
							<Button
								danger
								type="text"
								icon={<CustomIcon icon={IconTrash} />}
								onClick={onDelete}
							/>
						</Tooltip>
					)}
					<Button
						type="primary"
						onClick={() =>
							form.validateFields().then((values) => onSave(values))
						}
					>
						Save
					</Button>
				</Space>
			}
		>
			<Form form={form} layout="vertical" initialValues={initialValue}>
				<Form.Item
					name="url"
					label="URL"
					rules={[{ required: true, type: "url" }]}
				>
					<Input placeholder="https://..." />
				</Form.Item>
				<Form.Item name="name" label="Name">
					<Input placeholder="Link name" />
				</Form.Item>
				<Form.Item name="type" label="Type" rules={[{ required: true }]}>
					<Select options={toOptions(LinkTypeLabelMap)} />
				</Form.Item>
				<Form.Item name="description" label="Description">
					<Input.TextArea rows={2} placeholder="Optional description" />
				</Form.Item>
			</Form>
		</CustomDrawer>
	);
}

/**
 * Card grid of resource links; clicking a card opens a drawer to edit or delete it.
 * Shared by project and task link tabs — `onSave` persists the whole next array.
 */
export function ResourceLinksGrid({
	links,
	onSave,
	onAdd,
}: {
	links: ResourceLink[];
	onSave: (links: ResourceLink[]) => void;
	/** Opens the add-link dialog from the empty state. */
	onAdd?: () => void;
}) {
	const [editing, setEditing] = useState<number | null>(null);
	const close = () => setEditing(null);

	const saveLink = (value: ResourceLink) => {
		if (editing === null) return;
		onSave(links.map((l, i) => (i === editing ? value : l)));
		close();
	};
	const deleteLink = () => {
		if (editing === null) return;
		onSave(links.filter((_, i) => i !== editing));
		close();
	};

	if (links.length === 0)
		return <CustomEmpty description="Links" onAdd={onAdd} />;

	return (
		<>
			<CardViewer<ResourceLink & { index: number }>
				items={links.map((link, index) => ({ ...link, index }))}
				getKey={(i) => `${i.index}`}
				onOpen={(i) => setEditing(i.index)}
				renderCard={(i) => (
					<CardBody
						title={i.name || i.url || ""}
						extra={<LinkTypeBadge type={i.type} />}
					/>
				)}
			/>

			<LinkDrawer
				open={editing !== null}
				initialValue={editing !== null ? links[editing] : EMPTY_LINK}
				onSave={saveLink}
				onDelete={deleteLink}
				onClose={close}
			/>
		</>
	);
}
